import { CalendarOutlined, DeleteOutlined, FileAddOutlined } from '@ant-design/icons'
import { useRateManagement } from '@api/useFreight/useFreight'
import type { ManualAccessorial, ManualAccessorialRate } from '@api/useFreight/types'
import { RateStatusTag } from '@components/Freight/Ratebooks/Drill-in/components/RateStatusTag'
import { ManualAccessorialScheduleButton } from '@components/Freight/Ratebooks/Drill-in/components/ScheduleRateButton'
import { BBDTag, GraviButton, GraviGrid, Horizontal, MaterialIcon, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import type { ColDef, GridApi } from 'ag-grid-community'
import { DatePicker, Form, InputNumber, message, Popconfirm, Popover, Switch, Tooltip } from 'antd'
import dayjs from 'dayjs'
import type * as React from 'react'
import { type MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'

// Custom hook to use localStorage for persistence
function useLocalStorage<T>(key: string, initialValue: T): [T, (value: T) => void] {
  // State to store our value
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(key)
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue
    } catch (error) {
      // If error also return initialValue
      console.error('Error reading from localStorage:', error)
      return initialValue
    }
  })

  // Return a wrapped version of useState's setter function that
  // persists the new value to localStorage.
  const setValue = (value: T) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value
      // Save state
      setStoredValue(valueToStore)
      // Save to local storage
      window.localStorage.setItem(key, JSON.stringify(valueToStore))
    } catch (error) {
      // A more advanced implementation would handle the error case
      console.error('Error writing to localStorage:', error)
    }
  }

  return [storedValue, setValue]
}

// Component for deleting a scheduled rate
function DeleteRateButton({
  rate,
  accessorial,
  onSuccess,
}: {
  rate: ManualAccessorialRate
  accessorial: ManualAccessorial
  onSuccess?: () => void
}) {
  const queryClient = useQueryClient()
  const { useDeleteManualAccessorialRate } = useRateManagement()
  const deleteRateMutation = useDeleteManualAccessorialRate()

  const handleDeleteRate = async () => {
    try {
      // Use the correct ID field from the accessorial
      const accessorialId = accessorial._id || accessorial.id

      await deleteRateMutation.mutateAsync({
        accessorial_id: accessorialId as string,
        effective_date: rate.effective_from,
      })

      // Directly invalidate queries
      await queryClient.invalidateQueries({
        queryKey: ['manual_accessorials'],
      })
      message.success('Rate deleted successfully')

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      console.error('Error deleting rate:', error)
      message.error('Failed to delete rate')
    }
  }

  return (
    <Popconfirm
      title='Delete Scheduled Rate'
      description='Are you sure you want to delete this scheduled rate?'
      onConfirm={handleDeleteRate}
      okText='Yes'
      cancelText='No'
      okButtonProps={{ danger: true }}
    >
      <GraviButton error icon={<DeleteOutlined />} />
    </Popconfirm>
  )
}

// Component for editing an existing rate
function EditRateButton({
  rate,
  accessorial,
  onSuccess,
  onAccessorialUpdate,
}: {
  rate: ManualAccessorialRate
  accessorial: ManualAccessorial
  onSuccess?: () => void
  onAccessorialUpdate?: (updated: ManualAccessorial) => void
}) {
  const queryClient = useQueryClient()
  const [form] = Form.useForm<{
    rate: number
    effective_date: dayjs.Dayjs
    effective_to?: dayjs.Dayjs
  }>()
  const [popoverVisible, setPopoverVisible] = useState(false)
  const [showEffectiveTo, setShowEffectiveTo] = useLocalStorage('manual-accessorial-show-effective-to', false)

  const { useEditManualAccessorialRate } = useRateManagement()
  const editRateMutation = useEditManualAccessorialRate()

  const isPercentageType = accessorial.fee_type?.type?.includes('%') || false

  // When showEffectiveTo changes, we need to update form validation
  useEffect(() => {
    // Reset the effective_to field when the switch is toggled off
    if (!showEffectiveTo) {
      form.setFieldValue('effective_to', undefined)
    }

    // This will force the form to revalidate
    form.validateFields(['effective_to']).catch((e) => {
      // We can ignore validation errors here
    })
  }, [showEffectiveTo, form])

  const handleEditRate = async (values: {
    rate: number
    effective_date: dayjs.Dayjs
    effective_to?: dayjs.Dayjs
  }) => {
    try {
      // Format the dates as ISO strings
      const effectiveFromDate = values.effective_date.format('YYYY-MM-DD')
      const effectiveToDate =
        showEffectiveTo && values.effective_to ? values.effective_to.format('YYYY-MM-DD') : undefined

      // Use the correct ID field
      const accessorialId = accessorial._id || accessorial.id

      // Convert percentage to decimal for API if it's a percentage type
      const apiRate = isPercentageType ? Number((values.rate / 100).toFixed(3)) : values.rate

      // Ensure we have a valid rate_id
      if (!rate.id) {
        message.error('Rate ID is missing. Cannot update rate.')
        return
      }

      // Use the edit rate mutation with the correct payload schema
      const updatedAccessorial = await editRateMutation.mutateAsync({
        accessorial_id: accessorialId as string,
        effective_from_date: effectiveFromDate,
        effective_to_date: effectiveToDate,
        rate: apiRate,
        rate_id: rate.id,
      })

      // Success notification
      message.success('Rate updated successfully')

      // Close popover
      setPopoverVisible(false)

      // Update the local state with the new data
      if (onAccessorialUpdate) {
        onAccessorialUpdate(updatedAccessorial)
      }

      // Call onSuccess if provided
      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      console.error('Failed to update rate:', error)
      message.error('Failed to update rate')
    }
  }

  const renderPopoverContent = () => (
    <Form
      form={form}
      layout='vertical'
      onFinish={handleEditRate}
      initialValues={{
        effective_date: dayjs(rate.effective_from),
        effective_to: rate.effective_to ? dayjs(rate.effective_to as string) : undefined,
        // Convert decimal to percentage for display if it's a percentage type
        rate: isPercentageType ? rate.rate * 100 : rate.rate,
      }}
    >
      <Form.Item
        name='rate'
        label='Rate'
        rules={[
          { required: true, message: 'Please enter rate' },
          {
            validator: (_, value) => {
              if (isPercentageType && (value < 0 || value > 200)) {
                return Promise.reject('Percentage must be between 0 and 200')
              }
              return Promise.resolve()
            },
          },
        ]}
      >
        <InputNumber
          prefix={isPercentageType ? null : '$'}
          suffix={isPercentageType ? '%' : null}
          precision={isPercentageType ? 3 : 5}
          max={isPercentageType ? 200 : undefined}
          style={{ width: '100%' }}
        />
      </Form.Item>

      <Form.Item
        name='effective_date'
        label='Effective From'
        rules={[{ required: true, message: 'Please select effective date' }]}
      >
        <DatePicker format='MM/DD/YYYY' style={{ width: '100%' }} />
      </Form.Item>

      <Horizontal gap={8} justifyContent='flex-end'>
        <GraviButton appearance='outlined' onClick={() => setPopoverVisible(false)}>
          Cancel
        </GraviButton>
        <GraviButton appearance='solid' theme2 htmlType='submit'>
          Save
        </GraviButton>
      </Horizontal>
    </Form>
  )

  return (
    <Popover
      content={renderPopoverContent()}
      title='Edit Rate'
      trigger='click'
      placement='bottomLeft'
      open={popoverVisible}
      onOpenChange={setPopoverVisible}
    >
      <GraviButton icon={<MaterialIcon className='pt-1' size={16} icon='edit' />} />
    </Popover>
  )
}

// Add new ChangeEffectiveDateButton component
function ChangeEffectiveDateButton({
  rate,
  accessorial,
  onSuccess,
  onAccessorialUpdate,
}: {
  rate: ManualAccessorialRate
  accessorial: ManualAccessorial
  onSuccess?: () => void
  onAccessorialUpdate?: (updated: ManualAccessorial) => void
}) {
  const [form] = Form.useForm()
  const [popoverVisible, setPopoverVisible] = useState(false)
  const [showEffectiveTo, setShowEffectiveTo] = useLocalStorage('manual-accessorial-show-effective-to', false)

  const { useEditManualAccessorialRate } = useRateManagement()
  const editRateMutation = useEditManualAccessorialRate()

  // When showEffectiveTo changes, we need to update form validation
  useEffect(() => {
    if (!showEffectiveTo) {
      form.setFieldValue('effective_to', undefined)
    }
    form.validateFields(['effective_to']).catch(() => {
      // Ignore validation errors
    })
  }, [showEffectiveTo, form])

  const handleChangeEffective = async (values: {
    effective_date: dayjs.Dayjs
    effective_to?: dayjs.Dayjs
  }) => {
    try {
      const effectiveFromDate = values.effective_date.format('YYYY-MM-DD')
      const effectiveToDate =
        showEffectiveTo && values.effective_to ? values.effective_to.format('YYYY-MM-DD') : undefined

      const accessorialId = accessorial._id || accessorial.id

      // Use the existing rate value but update the effective dates
      const updatedAccessorial = await editRateMutation.mutateAsync({
        accessorial_id: accessorialId as string,
        effective_from_date: effectiveFromDate,
        effective_to_date: effectiveToDate,
        rate: rate.rate,
        rate_id: rate.id as string,
      })

      message.success('Effective date updated successfully')
      setPopoverVisible(false)

      if (onAccessorialUpdate) {
        onAccessorialUpdate(updatedAccessorial)
      }

      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      console.error('Failed to update effective date:', error)
      message.error('Failed to update effective date')
    }
  }

  const renderPopoverContent = () => (
    <Form
      form={form}
      layout='vertical'
      onFinish={handleChangeEffective}
      initialValues={{
        effective_date: dayjs(rate.effective_from),
        effective_to: rate.effective_to_date ? dayjs(rate.effective_to_date as string) : undefined,
      }}
    >
      <Form.Item
        name='effective_date'
        label='Effective From'
        rules={[{ required: true, message: 'Please select effective date' }]}
      >
        <DatePicker format='MM/DD/YYYY' style={{ width: '100%' }} />
      </Form.Item>

      <Horizontal gap={8} justifyContent='flex-end'>
        <GraviButton appearance='outlined' onClick={() => setPopoverVisible(false)}>
          Cancel
        </GraviButton>
        <GraviButton appearance='solid' theme2 htmlType='submit'>
          Save
        </GraviButton>
      </Horizontal>
    </Form>
  )

  return (
    <Popover
      content={renderPopoverContent()}
      title='Change Effective Date'
      trigger='click'
      placement='bottomLeft'
      open={popoverVisible}
      onOpenChange={setPopoverVisible}
    >
      <GraviButton icon={<CalendarOutlined />} />
    </Popover>
  )
}

export interface RateHistoryDrawerProps {
  accessorial: ManualAccessorial
  onRateChange?: () => void
}

// Helper function to check if fee type is percentage based
const isPercentageFeeType = (type: string): boolean => {
  return type?.includes('%') || false
}

export function RateHistoryDrawer({ accessorial: initialAccessorial, onRateChange }: RateHistoryDrawerProps) {
  const gridRef = useRef<GridApi>(null)
  const [accessorial, setAccessorial] = useState(initialAccessorial)
  const isPercentageType = accessorial.fee_type?.type?.includes('%') || false

  // Update local state when prop changes
  useEffect(() => {
    setAccessorial(initialAccessorial)
  }, [initialAccessorial])

  // Handle accessorial updates
  const handleAccessorialUpdate = useCallback((updated: ManualAccessorial) => {
    setAccessorial(updated)
  }, [])

  // Find the most recent active rate to properly mark only one rate as "active"
  const mostRecentActiveRateId = useMemo(() => {
    // If accessorial is disabled, no rates should be active
    if (accessorial.disabled || !accessorial.rates?.length) return null

    const now = dayjs()

    // Filter for rates that are effective now (not scheduled)
    const currentRates = accessorial.rates.filter((r) => {
      const effectiveFrom = dayjs(r.effective_from)
      const effectiveTo = r.effective_to ? dayjs(r.effective_to as string) : null

      // A rate is current if:
      // 1. It started in the past or today
      const hasStarted = effectiveFrom.isBefore(now) || effectiveFrom.isSame(now, 'day')
      // 2. And either has no end date or hasn't ended yet
      const hasNotEnded = !effectiveTo || effectiveTo.isAfter(now)

      return hasStarted && hasNotEnded
    })

    // Sort by effective_from date in descending order to get most recent first
    const sortedRates = [...currentRates].sort((a, b) => {
      return dayjs(b.effective_from).valueOf() - dayjs(a.effective_from).valueOf()
    })

    // Return the ID of the most recent active rate
    if (sortedRates.length === 0) return null

    const mostRecentRate = sortedRates[0]
    return mostRecentRate.id
  }, [accessorial.disabled, accessorial.rates])

  // Handle rate changes (deletion or editing)
  const handleRateChange = useCallback(() => {
    if (onRateChange) {
      onRateChange()
    }
  }, [onRateChange])

  const historyColumnDefs = useMemo<ColDef<ManualAccessorialRate>[]>(
    () => [
      {
        headerName: 'Rate',
        field: 'rate',
        minWidth: 100,
        cellStyle: {
          textAlign: 'right',
        },
        type: 'numericColumn',
        cellRenderer: ({ value }: { value: number }) => {
          if (value === null || value === undefined) return <Texto appearance='hint'>N/A</Texto>
          const formattedValue = isPercentageType ? `${(value * 100).toFixed(3)}%` : `$${value.toFixed(5)}`
          return <BBDTag theme2>{formattedValue}</BBDTag>
        },
      },
      {
        headerName: 'Status',
        minWidth: 120,
        cellRenderer: ({ data }: { data: ManualAccessorialRate }) => {
          if (!data) return null
          // Only show active if the accessorial is not disabled and this is the most recent active rate
          const isActive = !accessorial.disabled && data.id === mostRecentActiveRateId
          return <RateStatusTag rate={data} isMostRecentActive={isActive} />
        },
      },
      {
        headerName: 'From',
        field: 'effective_from',
        minWidth: 90,
        cellRenderer: ({ value }: { value: string }) => <Texto>{dayjs(value).format('MM/DD/YY')}</Texto>,
      },
      {
        headerName: '',
        minWidth: 120,
        cellStyle: () => ({
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
        }),
        cellRenderer: ({ data }: { data: ManualAccessorialRate }) => {
          if (!data) return null

          const now = dayjs()
          const effectiveFrom = dayjs(data.effective_from)

          // Check if rate is scheduled (effective date is in the future)
          const isScheduled = effectiveFrom.isAfter(now)

          return (
            <Horizontal gap={8} verticalCenter style={{ overflow: 'visible', width: '100%' }}>
              <EditRateButton
                rate={data}
                accessorial={accessorial}
                onSuccess={handleRateChange}
                onAccessorialUpdate={handleAccessorialUpdate}
              />
              <ChangeEffectiveDateButton
                rate={data}
                accessorial={accessorial}
                onSuccess={handleRateChange}
                onAccessorialUpdate={handleAccessorialUpdate}
              />
              {isScheduled && <DeleteRateButton rate={data} accessorial={accessorial} onSuccess={handleRateChange} />}
            </Horizontal>
          )
        },
      },
    ],
    [accessorial, handleAccessorialUpdate, handleRateChange, isPercentageType, mostRecentActiveRateId]
  )

  // Get current active rate for display in header
  const currentRate = useMemo(() => {
    if (!accessorial.rates?.length) return null

    const now = dayjs()
    const activeRates = accessorial.rates.filter((r) => {
      const effectiveFrom = dayjs(r.effective_from)
      return effectiveFrom.isBefore(now) || effectiveFrom.isSame(now, 'day')
    })

    if (activeRates.length === 0) return null

    const rate = activeRates.sort((a, b) => dayjs(b.effective_from).valueOf() - dayjs(a.effective_from).valueOf())[0]

    if (isPercentageType) {
      return `${(rate.rate * 100).toFixed(3)}%`
    }
    return `$${rate.rate.toFixed(5)}`
  }, [accessorial.rates, isPercentageType])

  return (
    <Vertical flex={1}>
      <Vertical
        flex='0'
        style={{ overflow: 'visible' }}
        border='all'
        borderRadius='4px'
        bg='elevated'
        className='p-3'
        gap={8}
      >
        <Texto category='h5' weight='600'>
          {accessorial.name}
        </Texto>

        {accessorial.description && (
          <Texto appearance='hint' style={{ marginBottom: 8 }}>
            {accessorial.description}
          </Texto>
        )}

        <Horizontal justifyContent='space-between'>
          <Horizontal gap={24}>
            <Vertical>
              <Texto appearance='hint'>Product Group</Texto>
              <BBDTag theme2>{accessorial.product_group || 'Any'}</BBDTag>
            </Vertical>
          </Horizontal>
          <Horizontal verticalCenter gap={8}>
            <ManualAccessorialScheduleButton
              accessorial={accessorial}
              onScheduleSuccess={onRateChange}
              initialRate={
                isPercentageType && accessorial.rates?.length
                  ? accessorial.rates[0].rate * 100
                  : accessorial.rates?.[0]?.rate
              }
            />
          </Horizontal>
        </Horizontal>
      </Vertical>

      <Vertical flex={1}>
        <GraviGrid
          key={`rate-history-${accessorial._id || accessorial.id}-${accessorial.rates.length}`}
          storageKey='manual_accessorial_rate_his'
          rowData={accessorial.rates}
          agPropOverrides={{
            getRowId: (params) => params.data.id as string,
            rowGroupPanelShow: 'never',
            rowHeight: 40,
            columnDefs: historyColumnDefs,
          }}
          controlBarProps={{
            title: `Rate History (${accessorial.rates.length})`,
            customSearchBar: <div />,
          }}
          externalRef={gridRef as MutableRefObject<GridApi>}
        />
      </Vertical>
    </Vertical>
  )
}
